/**
 * 霓虹蛇境 (Neon Snake Realm) - 得分面板组件
 *
 * 显示当前得分、历史最高分和连击指示器。
 * 从 App.tsx 中提取的纯展示组件，无内部状态。
 *
 * 使用方式：
 * ```tsx
 * <ScoreBoard score={gameState.score} />
 * ```
 */

import React from 'react';
import type { ScoreState } from '@/types/game';

// ============================================================================
// Props 类型定义
// ============================================================================

/**
 * ScoreBoard 组件的 Props 接口。
 *
 * 组件仅依赖 ScoreState 数据，不包含任何回调或事件处理。
 * 所有显示逻辑由父组件通过 props 驱动。
 */
export interface ScoreBoardProps {
  /** 当前得分状态（包含当前分、最高分、连击数等） */
  score: ScoreState;
}

// ============================================================================
// ScoreBoard 组件
// ============================================================================

/**
 * 得分面板组件。
 *
 * 渲染三个信息区域：
 * 1. 当前得分 - 霓虹绿色大号数字
 * 2. 最高分 - 金色大号数字
 * 3. 连击指示器 - 仅在连击数 > 1 时显示，带脉冲动画
 *
 * 组件为纯展示组件，不包含任何业务逻辑或副作用。
 * 所有样式通过全局 CSS 类名控制，组件本身不内联样式。
 *
 * @param props - 组件属性
 * @returns 得分面板的 JSX 元素
 */
const ScoreBoard: React.FC<ScoreBoardProps> = ({ score }) => {
  return (
    <div className="score-display">
      {/* ================================================================ */}
      {/* 当前得分 */}
      {/* ================================================================ */}
      <div className="score-item">
        <span className="score-label">得分</span>
        <span className="score-value">{score.current}</span>
      </div>

      {/* ================================================================ */}
      {/* 历史最高分 */}
      {/* ================================================================ */}
      <div className="score-item">
        <span className="score-label">最高分</span>
        <span className="score-value high-score">{score.highScore}</span>
      </div>

      {/* ================================================================ */}
      {/* 连击指示器（条件渲染） */}
      {/* ================================================================ */}
      {score.combo > 1 && (
        <div className="score-item combo-indicator">
          <span className="combo-text">{score.combo}x 连击!</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 导出
// ============================================================================

export default ScoreBoard;